
import React, { useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { FormInput } from '@/components/forms';
import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { ExamResult } from '@/utils/exam/types';
import { cleanScoreValue, detectPercentageAndConvert } from '@/utils/validation/examValidation';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';

interface ExamScoreFieldsProps {
  register: UseFormRegister<Partial<ExamResult>>;
  errors: FieldErrors<Partial<ExamResult>>;
  watchObtainedScore: number | string;
  watchMaxScore: number | string;
  percentage: string;
  setValue: UseFormSetValue<Partial<ExamResult>>;
  formSubmitted?: boolean;
  touchedFields?: Record<string, boolean>;
  studentPrograms?: any[];
}

const ExamScoreFields = ({
  register,
  errors,
  watchObtainedScore,
  watchMaxScore,
  percentage,
  setValue,
  formSubmitted = false,
  touchedFields = {},
  studentPrograms = []
}: ExamScoreFieldsProps) => {
  // Only show errors if form was submitted or field was touched and then blurred
  const shouldShowError = (fieldName: keyof ExamResult) => 
    (formSubmitted || touchedFields[fieldName]) && errors[fieldName];

  // Handle percentage detection in obtained score
  useEffect(() => {
    // Check if obtained score contains percentage character
    const obtainedScoreStr = String(watchObtainedScore);
    if (obtainedScoreStr.includes('%')) {
      // Extract the percentage value
      const maxScoreValue = cleanScoreValue(watchMaxScore);
      const calculatedScore = detectPercentageAndConvert(obtainedScoreStr, maxScoreValue);
      
      // Update the obtained score with the calculated value
      setValue('obtainedScore', calculatedScore);
    }
  }, [watchObtainedScore, watchMaxScore, setValue]);

  // Get score color based on percentage
  const getScoreColor = () => {
    const percentValue = parseFloat(percentage);
    if (percentValue >= 80) return 'bg-green-100 text-green-800 border-green-300';
    if (percentValue >= 70) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (percentValue >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (percentValue >= 40) return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  // Calculate live percentage for display - ensure we use cleaned number values
  const obtainedScoreValue = cleanScoreValue(watchObtainedScore);
  const maxScoreValue = cleanScoreValue(watchMaxScore);
  const calculatedPercentage = maxScoreValue > 0 
    ? ((obtainedScoreValue / maxScoreValue) * 100).toFixed(1) 
    : '0.0';

  return (
    <div className="space-y-6">
      <div className="rounded-md border p-4 bg-muted/20">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h3 className="text-sm font-medium">Exam Score</h3>
          
          {/* Score Percentage Display */}
          {maxScoreValue > 0 && (
            <Badge variant="outline" className={`px-3 py-1 ${getScoreColor()}`}>
              {calculatedPercentage}%
            </Badge>
          )}
        </div>
        
        {/* Progress Bar */}
        {maxScoreValue > 0 && (
          <div className="mb-6">
            <Progress 
              value={parseFloat(calculatedPercentage)} 
              className="h-2" 
              indicatorClassName={
                parseFloat(calculatedPercentage) >= 70 
                  ? "bg-green-500" 
                  : parseFloat(calculatedPercentage) >= 40 
                  ? "bg-yellow-500" 
                  : "bg-red-500"
              }
            />
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="obtainedScore" className="text-base">Obtained Score</Label>
            <FormInput
              id="obtainedScore"
              name="obtainedScore"
              inputType="text" // Text to allow % symbol
              className="h-12"
              {...register('obtainedScore')}
            />
            {shouldShowError('obtainedScore') && (
              <p className="text-xs text-red-500">{errors.obtainedScore?.message}</p>
            )}
            <p className="text-xs text-muted-foreground">You can enter a percentage (e.g. 80%)</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxScore" className="text-base">Maximum Score</Label>
            <FormInput
              id="maxScore"
              name="maxScore"
              inputType="decimal"
              className="h-12"
              {...register('maxScore')}
            />
            {shouldShowError('maxScore') && (
              <p className="text-xs text-red-500">{errors.maxScore?.message}</p>
            )}
          </div>
        </div>
        
        {/* Live Score Display */}
        <div className="mt-4 p-3 bg-muted/30 rounded-md">
          <p className="text-sm font-medium">Live Score Preview:</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-lg font-semibold">
              {obtainedScoreValue.toFixed(1)} / {maxScoreValue.toFixed(1)}
            </span>
            <span className="text-sm text-muted-foreground">
              ({calculatedPercentage}%)
            </span>
          </div>
        </div>
        
        {/* Score Matrix */}
        <div className="mt-6 pt-4 border-t">
          <p className="text-xs text-muted-foreground mb-2">Score Grading</p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">80%+ (Excellent)</Badge>
            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">70-79% (Good)</Badge>
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">60-69% (Average)</Badge>
            <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">40-59% (Needs Improvement)</Badge>
            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">&lt;40% (Fail)</Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamScoreFields;
